import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { lineTexture } from '../../Utils/localizedTexture.js'
import { t } from '../../../content/i18n.js'

/**
 * Domino gösterisi (girişin batısındaki boş alan): üç yapı, tek SIFIRLA alanı.
 *  1. Katlı: dominolar 3 basamaklı piramide tırmanır, düzlükte yürür, öbür yandan iner
 *  2. S kıvrımıyla içe dönen sarmal (zincir merkezde biter)
 *  3. Büyüyen hat: her taş öncekinin ~1.2 katı, sonda 3 m'lik dev domino
 * Basamaklar çalışma anında kurulan sabit kutular (zemin malzemesi: taşlar yerdeki gibi tutunur).
 * Dünya koordinatları; basamak/taş aralıkları sayfa içi fizik denemesiyle ayarlandı.
 */
const COLORS = ['blush', 'lilac', 'mint', 'ice', 'pistachio', 'raspberry', 'violet']
const SPACING = 0.56
const DOMINO = { x: 0.18, y: 0.62, z: 1 }

// Katlı piramit: y = 7.5 boyunca; her kat [x başlangıç (doğu), x bitiş (batı), üst yüz z, genişlik, renk]
const STEPS_Y = 7.5
const STEPS = [
    [- 31.9, - 46.1, 0.3, 2.6, 'shadeMint'],
    [- 33.5, - 44.5, 0.6, 2.2, 'shadeLilac'],
    [- 35.1, - 42.9, 0.9, 1.8, 'shadeBlush']
]

// Sarmal: merkez, dış ve iç yarıçap, tur sayısı
const SPIRAL = { x: - 52, y: - 3, r0: 5.5, r1: 1.7, turns: 1.2 }

// Büyüyen hat: y = -11 boyunca doğudan batıya
const GROWTH = { x: - 28, y: - 11, scales: [1, 1, 1, 1.2, 1.44, 1.73, 2.07, 2.49, 2.99] }

export default class DominoShowSection
{
    constructor(_options)
    {
        // Options
        this.resources = _options.resources
        this.objects = _options.objects
        this.materials = _options.materials
        this.areas = _options.areas

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.dominoes = []

        this.setSteps()
        this.setMainChain()
        this.setGrowth()
        this.setReset()
    }

    // Yerden yükseklik (piramit katları üst üste: en yüksek eşleşen kat)
    heightAt(_x, _y)
    {
        let height = 0
        for(const [_east, _west, _top, _width] of STEPS)
        {
            if(_x <= _east && _x >= _west && Math.abs(_y - STEPS_Y) <= _width / 2)
            {
                height = Math.max(height, _top)
            }
        }
        return height
    }

    setSteps()
    {
        const base = new THREE.Group()
        const collision = new THREE.Group()

        for(const [_east, _west, _top, _width, _material] of STEPS)
        {
            const length = _east - _west
            const center = new THREE.Vector3((_east + _west) / 2, STEPS_Y, _top / 2)

            const mesh = new THREE.Mesh(new RoundedBoxGeometry(length, _width, _top, 2, 0.05), new THREE.MeshBasicMaterial())
            mesh.name = _material
            mesh.position.copy(center)
            base.add(mesh)

            const cube = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1))
            cube.name = 'cube'
            cube.scale.set(length, _width, _top)
            cube.position.copy(center)
            collision.add(cube)
        }

        this.objects.add({
            base,
            collision,
            offset: new THREE.Vector3(0, 0, 0),
            mass: 0,
            physicsMaterial: 'floor'
        })
    }

    // Eşit aralıklı taş konumları: yol fonksiyonu (u ∈ [0, 1]) boyunca yay uzunluğuyla
    sample(_path, _spacing, _start = _spacing)
    {
        const points = []
        const steps = 4000
        let previous = _path(0)
        let travelled = _start
        for(let i = 0; i <= steps; i++)
        {
            const point = _path(i / steps)
            travelled += point.distanceTo(previous)
            previous = point
            if(travelled >= _spacing)
            {
                travelled = 0
                const ahead = _path(Math.min(1, i / steps + 0.0005))
                const behind = _path(Math.max(0, i / steps - 0.0005))
                points.push({ point, angle: Math.atan2(ahead.y - behind.y, ahead.x - behind.x) })
            }
        }
        return points
    }

    setMainChain()
    {
        // 1. Düz giriş + katlı piramit: y = 7.5, x -27.5 → -48
        const start = new THREE.Vector2(- 27.5, STEPS_Y)
        const end = new THREE.Vector2(- 48, STEPS_Y)
        const straight = this.sample((_u) => start.clone().lerp(end, _u), SPACING, SPACING)

        // Taş basamak kenarına binmesin: yüksekliği değişen kenara 0.16 m'den yakınsa kenardan uzağa it
        const risers = STEPS.flatMap(([_east, _west]) => [_east, _west])
        for(const _item of straight)
        {
            for(const _riser of risers)
            {
                const delta = _item.point.x - _riser
                if(Math.abs(delta) < 0.16)
                {
                    _item.point.x = _riser + (delta >= 0 ? 0.16 : - 0.16)
                }
            }
        }

        // 2. S kıvrımı: piramit sonundan sarmal girişine (ikisi de batıya bakar)
        const spiralEntry = new THREE.Vector2(SPIRAL.x, SPIRAL.y + SPIRAL.r0)
        const p0 = end.clone()
        const p1 = end.clone().add(new THREE.Vector2(- 2.6, 0))
        const p2 = spiralEntry.clone().add(new THREE.Vector2(2.6, 0))
        const p3 = spiralEntry.clone()
        const curve = new THREE.CubicBezierCurve(p0, p1, p2, p3)
        const bend = this.sample((_u) => curve.getPoint(_u), SPACING, 0)

        // 3. İçe dönen sarmal: girişte batıya gider (açı artarken)
        const spiral = this.sample((_u) =>
        {
            const angle = Math.PI * 0.5 + _u * SPIRAL.turns * Math.PI * 2
            const radius = SPIRAL.r0 - (SPIRAL.r0 - SPIRAL.r1) * _u
            return new THREE.Vector2(SPIRAL.x + Math.cos(angle) * radius, SPIRAL.y + Math.sin(angle) * radius)
        }, SPACING, 0)

        const chain = [...straight, ...bend, ...spiral]
        chain.forEach((_item, _index) => this.addDomino(_item.point, _item.angle, 1, COLORS[_index % COLORS.length]))
        this.mainCount = chain.length
    }

    setGrowth()
    {
        // Her taş bir öncekinin boyunun ~yarısından biraz fazlası kadar ötede: düşen taş sonrakinin üst yarısına değer
        let x = GROWTH.x
        GROWTH.scales.forEach((_scale, _index) =>
        {
            this.addDomino(new THREE.Vector2(x, GROWTH.y), Math.PI, _scale, COLORS[(_index + 2) % COLORS.length])
            const next = GROWTH.scales[_index + 1] || _scale
            x -= SPACING * Math.max(_scale, 1) * 0.5 + SPACING * next * 0.55
        })
        this.growthEnd = x
    }

    // Boyutlu domino sahnesi (ölçek başına bir kez; Objects duplicated ile kopyalar)
    dominoScenes(_scale)
    {
        this.scenes = this.scenes || {}
        if(!this.scenes[_scale])
        {
            if(_scale === 1)
            {
                this.scenes[_scale] = { base: this.resources.items.toysDominoBase.scene, collision: this.resources.items.toysDominoCollision.scene }
            }
            else
            {
                const size = new THREE.Vector3(DOMINO.x, DOMINO.y, DOMINO.z).multiplyScalar(_scale)
                const base = new THREE.Group()
                const mesh = new THREE.Mesh(new RoundedBoxGeometry(size.x, size.y, size.z, 2, 0.03 * _scale), new THREE.MeshBasicMaterial())
                mesh.name = 'shadeWhite'
                mesh.position.z = size.z / 2
                base.add(mesh)

                const collision = new THREE.Group()
                const cube = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1))
                cube.name = 'cube'
                cube.scale.copy(size)
                cube.position.z = size.z / 2
                const center = new THREE.Object3D()
                center.name = 'center'
                center.position.z = size.z / 2
                collision.add(cube, center)

                this.scenes[_scale] = { base, collision }
            }
        }
        return this.scenes[_scale]
    }

    addDomino(_point, _angle, _scale, _color)
    {
        const scenes = this.dominoScenes(_scale)
        const z = this.heightAt(_point.x, _point.y)
        const domino = this.objects.add({
            base: scenes.base,
            collision: scenes.collision,
            offset: new THREE.Vector3(_point.x, _point.y, z + 0.001),
            rotation: new THREE.Euler(0, 0, _angle),
            duplicated: true,
            material: this.materials.items[_color],
            // Basamak üstündekilerin gölgesi yere düşmez (kutunun içinde kalırdı)
            shadow: z > 0 ? undefined : { sizeX: 0.5 * _scale, sizeY: 0.9 * _scale, offsetZ: - 0.4 * _scale, alpha: 0.3 },
            mass: 0.4 * _scale * _scale * _scale,
            physicsMaterial: 'domino',
            soundName: _scale > 1.5 ? 'blockHeavy' : 'wood'
        })
        this.dominoes.push(domino)
        return domino
    }

    setReset()
    {
        // Girişin kuzeyinde (ilk taşın doğusu)
        this.resetArea = this.areas.add({
            position: new THREE.Vector2(- 25, 10.8),
            halfExtents: new THREE.Vector2(1.3, 1.3)
        })
        this.resetArea.on('interact', () =>
        {
            this.reset()
        })

        this.resetTexture = lineTexture(() => t('floorReset'))
        const label = new THREE.Mesh(
            new THREE.PlaneGeometry(3.4, 0.85),
            new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.resetTexture })
        )
        label.position.set(- 25, 8.9, 0.002)
        label.matrixAutoUpdate = false
        label.updateMatrix()
        this.container.add(label)
    }

    reset()
    {
        for(const _item of this.dominoes)
        {
            _item.collision.reset()
            _item.collision.body.velocity.set(0, 0, 0)
            _item.collision.body.angularVelocity.set(0, 0, 0)
        }
    }
}
